// src/pages/competition/JudgeSelection.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from './CompetitionContext';
import { usePolling } from '../../context/PollingContext';
import { Loader2, CheckCircle, Clock, AlertCircle, User, Search, ArrowLeft, Award, Sparkles, Zap } from 'lucide-react';
import { JUDGES_API, PHOTOS_API, fetchWithAuth } from '../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import ddLogo from '../../components/ui/dd.png';

const JudgeSelection = () => {
  const navigate = useNavigate();
  const { saveCompetitionData } = useCompetition();
  const { connected, startSession, setJudgeProfile } = usePolling();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCompetitions, setActiveCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [judgePosition, setJudgePosition] = useState('');
  const [availableJudges, setAvailableJudges] = useState([]);
  const [selectedJudge, setSelectedJudge] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingJudges, setIsLoadingJudges] = useState(false);
  const [failedImages, setFailedImages] = useState({});

  // Fetch active competitions from server
  useEffect(() => {
    console.log('JudgeSelection useEffect - connected:', connected);
    
    if (!connected) {
      setError('Waiting for connection to server...');
      return;
    }

    // Note: getActiveCompetitions removed in simplified approach
    setError('This page is deprecated. Please use the Competition Portal to start a session.');
    return;

    setIsLoading(true);
    
    // Fetch active competitions from polling API - DEPRECATED
    const fetchActiveCompetitions = async () => {
      try {
        console.log('Fetching active competitions - DEPRECATED');
        // getActiveCompetitions removed in simplified approach
        const competitions = [];
        
        console.log('Received competitions:', competitions);
        
        // Transform the data for our UI
        const formattedCompetitions = competitions.map(comp => ({
          id: comp.competitionCode,
          displayName: comp.displayName,
          eventName: comp.eventName,
          stageName: comp.stageName,
          tabulatorConnected: comp.hasTabulatorConnected,
          lastActive: comp.lastActivity,
          judgeCount: comp.judgeCount,
          connectedJudges: comp.connectedJudges || [],
          judges: {
            '1': { connected: (comp.connectedJudges || []).includes('1'), lastActive: null },
            '2': { connected: (comp.connectedJudges || []).includes('2'), lastActive: null },
            '3': { connected: (comp.connectedJudges || []).includes('3'), lastActive: null }
          }
        }));
        
        setActiveCompetitions(formattedCompetitions);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch active competitions:', error);
        setActiveCompetitions([]);
        setIsLoading(false);
      }
    };
    
    fetchActiveCompetitions();
  }, [connected]);

  // Helper function to refresh photo URLs
  const refreshPhotoUrl = async (photoUrl) => {
    if (!photoUrl) return null;

    try {
      const baseUrl = photoUrl.split("?")[0];
      const key = baseUrl.split(".com/")[1];
      if (!key) return photoUrl; // If we can't extract the key, return the original URL
      
      const data = await fetchWithAuth(`${PHOTOS_API}?key=${key}`);
      return data.url;
    } catch (error) {
      console.error("Error in refreshPhotoUrl:", error);
      return photoUrl; // Return original URL if refresh fails
    }
  };

  // Fetch judge profiles from admin account 
  useEffect(() => {
    const fetchJudges = async () => {
      try {
        setIsLoadingJudges(true);
        setError('');
        
        const data = await fetchWithAuth(JUDGES_API);
        console.log("JudgeSelection - Raw judge data from API:", data);
        
        // Check if defaultNote is in the raw data
        data.forEach((judge, index) => {
          console.log(`JudgeSelection - Raw judge ${index} defaultNote:`, judge.defaultNote);
        });
        
        // Format judge data with async map to handle URL refreshing
        const processedJudges = await Promise.all(data.map(async judge => {
          // Your DB stores the ID in SK as "JUDGE#..."
          const judgeId = judge.SK ? judge.SK.replace("JUDGE#", "") : undefined;

          // Collect all possible photo URL properties
          const photoUrl = judge.photoUrl || judge.photo || "";
          
          // Create the processed judge object
          const processedJudge = {
            id: judgeId,
            name: judge.name || "Unnamed Judge",
            title: judge.title || "",
            defaultNote: judge.defaultNote || "",
            photoUrl: photoUrl, // Store original URL
          };
          
          // Refresh the photo URL if it exists
          if (photoUrl) {
            try {
              processedJudge.photoUrl = await refreshPhotoUrl(photoUrl);
            } catch (err) {
              console.error("Failed to refresh photo URL for judge:", judgeId, err);
              // Keep the original URL if refresh fails
            }
          }
          
          console.log("Processed judge:", processedJudge);
          return processedJudge;
        }));

        console.log("JudgeSelection - Processed judges:", processedJudges);
        
        // Check if defaultNote is preserved in processed data
        processedJudges.forEach((judge, index) => {
          console.log(`JudgeSelection - Processed judge ${index} (${judge.name}) defaultNote:`, judge.defaultNote);
        });
        
        setAvailableJudges(processedJudges);
        console.log("All available judges with processed photos:", processedJudges);
      } catch (error) {
        console.error("Error fetching judges:", error);
        // Continue with position-based selection even if judge profiles can't be loaded
      } finally {
        setIsLoadingJudges(false);
      }
    };

    fetchJudges();
  }, []);

  // Helper to generate a display name from competition code
  const getDisplayNameFromCode = (code) => {
    // Convert competition ID like "streetz-phoenix-stage1-friday" to a readable name
    if (!code) return 'Unknown Competition';
    
    const parts = code.split('-');
    if (parts.length < 3) return code; // Return as is if format doesn't match
    
    // Format based on known convention and city names
    const conventionMap = {
      'streetz': 'Streetz Dance Convention',
      'edge': 'The Edge Dance Competition'
    };
    
    const cityMap = {
      'phoenix': 'Phoenix, AZ',
      'losangeles': 'Los Angeles, CA',
      'chicago': 'Chicago, IL',
      'dallas': 'Dallas, TX'
    };
    
    const convention = conventionMap[parts[0]] || parts[0];
    const city = cityMap[parts[1]] || parts[1];
    const stage = parts[2].replace('stage', 'Stage ');
    const day = parts[3] ? parts[3].charAt(0).toUpperCase() + parts[3].slice(1) : '';
    
    return `${convention} - ${city} - ${stage} (${day})`;
  };

  const formatTime = (timeString, eventTimezone = null) => {
    if (!timeString) return 'Never';
    const date = new Date(timeString);
    const options = {};
    if (eventTimezone) {
      options.timeZone = eventTimezone;
    }
    return date.toLocaleTimeString('en-US', options);
  };

  const handleCompetitionSelect = (competition) => {
    setSelectedCompetition(competition);
    setJudgePosition(''); // Reset judge position when competition changes
    setSelectedJudge(null); // Reset selected judge when competition changes
  };

  const handleJoinCompetition = async () => {
    if (!selectedCompetition || !judgePosition) {
      setError('Please select a competition and judge position');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      console.log(`Joining competition ${selectedCompetition.id} as Judge ${judgePosition}`);
      
      // Save competition data to context/localStorage with judge profile if available
      const competitionData = {
        competitionId: selectedCompetition.id,
        competitionCode: selectedCompetition.id,
        role: 'judge',
        judgeId: judgePosition,
        displayName: selectedCompetition.displayName
      };
      
      // Add judge profile data if a judge was selected
      if (selectedJudge) {
        console.log("JudgeSelection - Selected judge profile:", selectedJudge);
        console.log("JudgeSelection - Selected judge defaultNote:", selectedJudge.defaultNote);
        
        competitionData.judgeProfile = {
          id: selectedJudge.id,
          name: selectedJudge.name,
          title: selectedJudge.title,
          defaultNote: selectedJudge.defaultNote,
          photoUrl: selectedJudge.photoUrl // Use the refreshed URL
        };
        
        console.log("JudgeSelection - Adding judge profile to competition data:", competitionData.judgeProfile);
        console.log("JudgeSelection - Competition data judgeProfile defaultNote:", competitionData.judgeProfile.defaultNote);
        
        // Note: In simplified approach, this functionality is deprecated
        // Users should use Competition Portal to start sessions directly
        throw new Error('This page is deprecated. Please use the Competition Portal to start a session.');
      }
      
      saveCompetitionData(competitionData);
      console.log("Saved competition data:", competitionData);
      
      // Navigate to judge station page
      navigate(`/competition/judge/${selectedCompetition.id}/${judgePosition}`, {
        state: {
          competitionId: selectedCompetition.id,
          competitionName: selectedCompetition.displayName,
          judgeProfile: selectedJudge
        }
      });
      
    } catch (err) {
      console.error('Error joining competition:', err);
      setError('Failed to join competition. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image load errors properly in React
  const handleImageError = (judgeId) => {
    console.log(`Image loading failed for judge ID: ${judgeId}`);
    setFailedImages(prev => ({
      ...prev,
      [judgeId]: true
    }));
  };

  // Filter judges based on search term
  const filteredJudges = searchTerm.trim() === '' 
    ? availableJudges 
    : availableJudges.filter(judge => 
        judge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        judge.title.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Stage Background - purple theme for judges */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-pink-900/20"></div>
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-purple-500/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>
      
      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="max-w-2xl w-full"
        >
          {/* Header with logo */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="relative"
              >
                <img 
                  src={ddLogo}
                  alt="Dance Digital"
                  className="w-16 h-16 object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl blur-xl opacity-60"></div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/40 text-base text-purple-200 mb-6 backdrop-blur-xl"
            >
              <Award className="w-4 h-4 mr-2" />
              <span className="font-semibold">Judge Station Setup</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 whitespace-nowrap"
            >
              <span className="text-white drop-shadow-2xl">JOIN </span>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent drop-shadow-2xl">COMPETITION</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-lg text-white/80 font-medium leading-relaxed mb-8 max-w-xl mx-auto"
            >
              Connect to an active competition with 
              <span className="text-purple-300 font-bold"> instant scoring</span> and 
              <span className="text-pink-300 font-bold"> real-time feedback</span>.
            </motion.p>
          </div>
          
          {/* Main content card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl"></div>
            
            {/* Card content */}
            <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl p-8">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-white flex items-center backdrop-blur-sm"
                >
                  <AlertCircle size={18} className="text-red-400 mr-3 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            
              {isLoading ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center p-12"
                >
                  <Loader2 className="h-10 w-10 animate-spin mb-4 text-purple-400" />
                  <p className="text-white/70 text-lg">Loading active competitions...</p>
                </motion.div>
            ) : (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <h2 className="text-2xl font-bold mb-6 text-white flex items-center">
                    <Sparkles className="w-6 h-6 mr-2 text-purple-400" />
                    Available Competitions
                  </h2>
                  
                  {activeCompetitions.length === 0 ? (
                    <div className="text-center p-8 border border-white/10 rounded-lg bg-white/5">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-400/80" />
                      <p className="text-lg mb-2">No Active Competitions</p>
                      <p className="text-sm text-white/60">
                        Ask a tabulator to set up a competition first.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 mb-6">
                      {activeCompetitions.map(competition => (
                        <motion.div 
                          key={competition.id}
                          onClick={() => handleCompetitionSelect(competition)}
                          whileHover={{ scale: 1.01 }}
                          className={`p-6 rounded-xl border cursor-pointer transition-all duration-300 ${
                            selectedCompetition?.id === competition.id
                              ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400/50 shadow-lg shadow-purple-500/25'
                              : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-purple-400/30'
                          }`}
                        >
                          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                            <div>
                              <h3 className="font-medium text-lg mb-1">{competition.displayName}</h3>
                              <div className="flex items-center text-sm text-white/70">
                                <Clock className="h-4 w-4 mr-1" />
                                <span>Active: {formatTime(competition.lastActive)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className={`px-3 py-1 rounded-full text-xs md:text-sm inline-flex items-center ${
                                competition.tabulatorConnected
                                  ? 'bg-green-500/20 text-green-300'
                                  : 'bg-yellow-500/20 text-yellow-300'
                              }`}>
                                <span className={`w-2 h-2 rounded-full mr-2 ${
                                  competition.tabulatorConnected ? 'bg-green-400' : 'bg-yellow-400'
                                }`}></span>
                                {competition.tabulatorConnected ? 'Tabulator Connected' : 'Tabulator Offline'}
                              </div>
                              <div className="text-xs text-white/50 hidden md:block">
                                Click to join →
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
                
                {selectedCompetition && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                  >
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold mb-4">Select Judge Position</h2>
                      
                      <div className={`grid gap-3 mb-4 ${
                        selectedCompetition.judgeCount <= 3 ? 'grid-cols-3' :
                        selectedCompetition.judgeCount === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                        'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
                      }`}>
                        {Array.from({ length: selectedCompetition.judgeCount || 3 }, (_, i) => i + 1).map(position => {
                          const isAvailable = !selectedCompetition.judges[position]?.connected;
                          return (
                            <motion.button
                              key={position}
                              onClick={() => isAvailable && setJudgePosition(position.toString())}
                              disabled={!isAvailable}
                              whileHover={isAvailable ? { scale: 1.03 } : {}}
                              whileTap={isAvailable ? { scale: 0.98 } : {}}
                              className={`p-3 rounded-lg border text-center transition-all ${
                                judgePosition === position.toString()
                                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 border-purple-400 text-white shadow-lg shadow-purple-500/25'
                                  : isAvailable
                                    ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-purple-400/30'
                                    : 'bg-gray-500/10 border-gray-500/20 text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              <div className="font-medium">Judge {position}</div>
                              <div className="text-xs mt-1 opacity-80">
                                {isAvailable ? 'Available' : 'Already Connected'}
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>

                    {judgePosition && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                        className="mb-6"
                      >
                        <h2 className="text-xl font-semibold mb-4">Select Judge Profile (Optional)</h2>
                        
                        {isLoadingJudges ? (
                          <div className="flex justify-center items-center p-4">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                          </div>
                        ) : availableJudges.length > 0 ? (
                          <>
                            <div className="relative mb-4">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-white/60" />
                              </div>
                              <input
                                type="text"
                                placeholder="Search judges..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-blue-500/50 focus:outline-none focus:ring-0 transition-colors"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                              {filteredJudges.map(judge => (
                                <motion.div
                                  key={judge.id}
                                  onClick={() => setSelectedJudge(judge)}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                    selectedJudge?.id === judge.id
                                      ? 'bg-blue-600/30 border-blue-500/50'
                                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-blue-500/20 flex items-center justify-center">
                                      {judge.photoUrl && !failedImages[judge.id] ? (
                                        <img 
                                          src={judge.photoUrl} 
                                          alt={judge.name}
                                          className="w-full h-full object-cover"
                                          onError={() => handleImageError(judge.id)}
                                        />
                                      ) : (
                                        <span className="text-lg text-blue-400">{judge.name.charAt(0)}</span>
                                      )}
                                    </div>
                                    <div className="ml-3 min-w-0">
                                      <div className="font-medium text-sm truncate">{judge.name}</div>
                                      <div className="text-xs text-white/60 truncate">{judge.title}</div>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            
                            {selectedJudge && (
                              <div className="mt-3 flex justify-center">
                                <motion.button 
                                  onClick={() => setSelectedJudge(null)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 rounded-lg text-white/70"
                                >
                                  Clear Selection
                                </motion.button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center p-6 border border-white/10 rounded-lg bg-white/5">
                            <User className="h-8 w-8 mx-auto mb-3 text-white/40" />
                            <p className="text-sm text-white/80">No judge profiles available</p>
                            <p className="text-xs text-white/60 mt-1">
                              You can continue without a profile or add judges in the admin section
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                )}
                
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/competition/portal')}
                    className="w-full sm:w-auto px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/80 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft size={18} />
                    <span>Back to Portal</span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleJoinCompetition}
                    disabled={!selectedCompetition || !judgePosition}
                    className={`w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-lg text-center transition-all flex items-center justify-center gap-3 ${
                      selectedCompetition && judgePosition
                        ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:shadow-2xl hover:shadow-purple-500/30 border border-purple-400/50'
                        : 'bg-white/5 text-white/30 cursor-not-allowed border border-white/10'
                    }`}
                  >
                    {selectedJudge ? (
                      <>
                        <Zap size={20} />
                        <span>Join as {selectedJudge.name}</span>
                      </>
                    ) : (
                      <>
                        <Zap size={20} />
                        <span>Join as Judge {judgePosition}</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </>
              )}
              
              {/* Back button */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/competition/portal')}
                  className="w-full py-3 px-6 rounded-xl font-semibold text-white/80 transition-all duration-300 flex items-center justify-center space-x-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20"
                >
                  <ArrowLeft size={18} />
                  <span>Back to Portal</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default JudgeSelection;